/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';

import AppNavMedliSearch from '../../components/appNav/AppNavMedliSearch';
import { iMedliSuggestionResult, iMedliSearchResult } from '../../../apiServices/apiModels/Medli';

import SearchResults from './components/SearchResults';
import { TextLink } from '../../components/TextLink';
import { ScrollAwareFixedHeader } from '../../components/ScrollAware';
import ContentFrame from '../../applicationFrame/ContentFrame';

import { useActiveUser } from '../../hooks/useUser';
import { useActiveProfile } from '../../hooks/useProfile';
import { useMedliSearch } from '../../hooks/useMedli';
import MedicalTermsModel from '../../../models/MedicalTermsModel';
import { eMedliCategoryName } from '../../../constants/MedliNames';
import ContentLoader from '../../components/ContentLoader';

interface ResultGroup {
  titleId: string;
  searchResults: iMedliSuggestionResult[] | iMedliSearchResult[];
}
interface Props {
  hasSuggestions: boolean;
  initialMedliLocale: {};
  allResults: ResultGroup[];
  curatedResults: ResultGroup[];
  nonCuratedResults: ResultGroup[];
  suggestedResults: ResultGroup[];
  totalFoundResults: number;
  queryPlaceholder: string;

  medliCategory: string;
  selectSearchResult(medliSearchResult, index, showSuggestions, cb): void;
  getInitialResults(): void;
  submitSearch(query, category, languageCode, profileId): void;
  clearSearch(): void;
  cancelSearch(): void;
  profileId: number;
  isSearching: boolean;
  isSearchComplete: boolean;
  query: string;
}
interface State {
  showAllResults: boolean;
  searchResultForDefinition: {}[];
  showSuggestions: boolean;
  searchSubmitted: boolean;
  medliLocale: {};
}

class SearchView extends React.Component<Props, State> {

  state: State = {
    showAllResults: false,
    searchResultForDefinition: null,
    showSuggestions: this.props.hasSuggestions,
    searchSubmitted: false,
    medliLocale: this.props.initialMedliLocale,
  };

  componentWillMount() {
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    } else {
      document.body.scrollTop = 0;
    }
  }

  componentWillUnmount() {
    this.props.clearSearch();
  }

  onChangeQuery = () => {
    this.props.clearSearch();
    this.setState({
      showSuggestions: false,
    });
  }

  getResultsForDisplay = () => {
    if (this.state.showSuggestions) {
      return this.props.suggestedResults;
    } else if (this.state.showAllResults || this.props.curatedResults[0].searchResults.length === 0) {
      return this.props.allResults;
    } else {
      return this.props.curatedResults;
    }
  }

  getValues() {
    const results = this.getResultsForDisplay();
    return results.map(r => ({
      searchResults: r.searchResults,
      header: r.titleId ? this.renderHeader(r.titleId) : null,
      footer: this.renderShowMore(r.searchResults.length),
    }));
  }

  selectSearchResult = _.throttle((medliSearchResult, index) => {
    this.props.selectSearchResult(medliSearchResult, index, this.state.showSuggestions, () => {});
  }, 3000, { leading: true, trailing: false });

  submitSearch = ({ query, category, languageCode }) => {
    this.props.submitSearch(query, category, languageCode, this.props.profileId);
    this.setState({
      showAllResults: false,
      searchSubmitted: true,
    });
  }

  shouldAutoFocus() {
    return !this.state.showSuggestions;
  }

  renderShowMore(displayedResultCount) {
    if (!this.props.isSearchComplete) {
      return null;
    } else if (this.state.showAllResults || this.props.curatedResults[0].searchResults.length === 0) {
      if (this.props.totalFoundResults > displayedResultCount) {
        return (
          <span className="font-footnote font-color-gsMedDark">
            <FormattedMessage id="search.moreResults.message.text" values={{ digit1: this.props.totalFoundResults - displayedResultCount }} />
          </span>
        );
      } else {
        return null;
      }
    } else if (this.props.nonCuratedResults[0].searchResults.length === 0) {
      return null;
    } else {
      return (
        <div>
          <TextLink className="font-color-profile-accent font-footnote" onClick={() => { this.setState({ showAllResults: true }); }}>
            <FormattedMessage id="search.more.button.label" />
          </TextLink>
        </div>
      );
    }
  }

  renderHeader(titleId) {
    return (
      <div className="font-caption-1 font-color-gsMedDark" style={{ textTransform: 'uppercase' }}>
        <FormattedMessage id={titleId} />
      </div>
    );
  }

  render() {
    const values = this.getValues();
    const hasValues = values.length > 0 ? values[0].searchResults.length > 0 : false;
    const suggestionsLoaded = this.state.showSuggestions ? hasValues : true;

    return (
      <div>
        <ScrollAwareFixedHeader colorThemeName="profile">
          <AppNavMedliSearch
            query={this.props.query}
            medliCategory={this.props.medliCategory}
            queryPlaceholder={this.props.queryPlaceholder}
            hasInitialFocus={this.shouldAutoFocus()}
            actions={{
              cancelSearch: this.props.cancelSearch,
              submitSearch: this.submitSearch,
              onChangeQuery: this.onChangeQuery,
            }}
          />
        </ScrollAwareFixedHeader>

        <ContentFrame>
          <ContentLoader isContentLoaded={suggestionsLoaded} blockRenderUntilLoaded={false} isFixed={true}>
            <SearchResults
              isSearching={this.props.isSearching}
              isSearchComplete={this.props.isSearchComplete}
              query={this.props.query}
              values={values}
              onSelectSearchResult={this.selectSearchResult}
            />
          </ContentLoader>
        </ContentFrame>
      </div>
    );
  }
}

interface MedliSearchViewProps {
  onSelectResult(d, isSuggestion): void;
  hasSuggestions: boolean;
  queryPlaceholder: string;
  suggestedResults: any[];
  medliCategory: eMedliCategoryName;
  medliSearchCategory: eMedliCategoryName;
  onGoBack(): void;
}
export const MedliSearchView: React.FC<MedliSearchViewProps> = (props) => {
  const { hasSuggestions, queryPlaceholder, suggestedResults, medliCategory, medliSearchCategory, onSelectResult, onGoBack } = props;

  const [{ preferredLanguageCode }] = useActiveUser();
  const [{ searchResults, resultsCount, isFetching, isLoaded, searchQuery }, medliSearchActions] = useMedliSearch();
  const curatedResults = searchResults.filter((r) => r.curated);
  const nonCuratedResults = searchResults.filter((r) => !r.curated);
  const [{ profileId }] = useActiveProfile();

  return (
    <SearchView
      hasSuggestions={hasSuggestions}
      medliCategory=""
      profileId={profileId}
      initialMedliLocale={preferredLanguageCode}
      queryPlaceholder={queryPlaceholder}
      totalFoundResults={resultsCount}
      isSearching={isFetching}
      isSearchComplete={isLoaded}
      query={searchQuery}
      allResults={[{
        titleId: 'search.results.heading',
        searchResults: searchResults.map((r) => ({ searchResult: new MedicalTermsModel(r) })),
      }]}
      curatedResults={[{
        titleId: 'search.results.heading',
        searchResults: curatedResults.map((r) => ({ searchResult: new MedicalTermsModel(r) })),
      }]}
      nonCuratedResults={[{
        titleId: 'search.results.heading',
        searchResults: nonCuratedResults.map((r) => ({ searchResult: new MedicalTermsModel(r) })),
      }]}
      suggestedResults={suggestedResults.map((sr) => ({
        searchResults: sr.results.map((r) => ({ searchResult: new MedicalTermsModel(r) })),
        titleId: sr.titleId || 'search.suggestions.heading',
      }))}
      submitSearch={(query, _category, medliLanguageCode) => {
        medliSearchActions.submitSearch(query, medliSearchCategory, medliLanguageCode);
      }}
      clearSearch={() => {
        medliSearchActions.clearSearch();
      }}
      getInitialResults={null}
      cancelSearch={() => {
        medliSearchActions.cancelSearch(medliCategory);
        onGoBack();
      }}
      selectSearchResult={(searchResult, index, isSuggestion) => {
        onSelectResult(searchResult, isSuggestion);
      }}
    />
  );
};

export default SearchView;
